/**
 * Diff matching for the ceník XLSX import review: pairs imported entity
 * drafts with the model's active catalog entities so the UI can render
 * Nová / Nahrazuje / Chybí v souboru states.
 *
 * Matching key is (provedeni bez MY přípony, motor, převodovka) — verze and
 * the exact MY-suffixed provedeni act as tiebreakers among candidates. Each
 * active entity can be claimed by at most one imported row (first wins);
 * with MY24 + MY26 sheets on one model this is what keeps a legacy row from
 * being "replaced" twice.
 */

import type { CenikImportEntityDraft, MgmtEntity } from '@/modules/management/api';

const norm = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

/** "Acenta MY26" → "Acenta" (model-year suffix per the import convention). */
export const bezMy = (provedeni: string) => provedeni.replace(/\s*MY\s?\d{2}.*$/i, '').trim();

const klic = (provedeni: string, motor: string, prevodovka: string) =>
  [norm(bezMy(provedeni)), norm(motor), norm(prevodovka)].join('|');

export interface CenikImportDiffRow {
  draft: CenikImportEntityDraft;
  /** Sheet the row came from (sections can merge several sheets per model). */
  list: string;
  /**
   * Position within its own sheet — with `list` it identifies the row no
   * matter how sheets regroup, so review state (exclusions, manually filled
   * interiors) survives a model remapping.
   */
  poradi: number;
  /** Matched active entity this row would replace; null = brand-new row. */
  nahrazuje: MgmtEntity | null;
}

export interface CenikImportDiff {
  rows: CenikImportDiffRow[];
  /** Active entities no imported row claimed — "chybí v souboru". */
  chybejici: MgmtEntity[];
}

export function diffEntities(
  drafts: { list: string; entity: CenikImportEntityDraft[] }[],
  active: MgmtEntity[],
): CenikImportDiff {
  const podleKlice = new Map<string, MgmtEntity[]>();
  for (const e of active) {
    const k = klic(e.provedeni, e.motor, e.prevodovka);
    const bucket = podleKlice.get(k);
    if (bucket) bucket.push(e);
    else podleKlice.set(k, [e]);
  }

  const claimed = new Set<number>();
  const rows: CenikImportDiffRow[] = [];

  for (const { list, entity } of drafts) {
    for (const [poradi, draft] of entity.entries()) {
      const kandidati = (podleKlice.get(klic(draft.provedeni, draft.motor, draft.prevodovka)) ?? [])
        .filter((e) => !claimed.has(e.id));
      // Tiebreakers: exact MY-suffixed provedeni first, then matching verze.
      const vitez =
        kandidati.find((e) => norm(e.provedeni) === norm(draft.provedeni)) ??
        kandidati.find((e) => norm(e.verze) === norm(draft.verze)) ??
        kandidati[0] ??
        null;
      if (vitez) claimed.add(vitez.id);
      rows.push({ draft, list, poradi, nahrazuje: vitez });
    }
  }

  return { rows, chybejici: active.filter((e) => !claimed.has(e.id)) };
}
